<template>
  <div class="auto-complete-input-container">
    <el-input
      v-model="inputValue"
      :placeholder="placeholder"
      @input="handleInput"
      @focus="showSuggestions = true"
      @blur="hideSuggestionsDelayed"
    >
      <template #suffix v-if="inputValue">
        <el-icon @click="clearInput">
          <Close />
        </el-icon>
      </template>
    </el-input>
    
    <div 
      v-if="showSuggestions && filteredSuggestions.length > 0" 
      class="suggestions-container"
    >
      <ul class="suggestions-list">
        <li 
          v-for="(suggestion, index) in filteredSuggestions" 
          :key="index"
          class="suggestion-item"
          @mousedown="selectSuggestion(suggestion)"
        >
          <span>{{ suggestion }}</span>
          <el-icon class="delete-icon" @mousedown.stop="deleteSuggestion(suggestion)">
            <Delete />
          </el-icon>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue';
import { Delete, Close } from '@element-plus/icons-vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  storageKey: {
    type: String,
    required: true
  },
  maxHistoryItems: {
    type: Number,
    default: 10
  }
});

const emit = defineEmits(['update:modelValue']);

const inputValue = ref(props.modelValue);
const suggestions = ref<string[]>([]);
const showSuggestions = ref(false);

// 监听modelValue变化
watch(() => props.modelValue, (newValue) => {
  inputValue.value = newValue;
});

// 监听inputValue变化
watch(() => inputValue.value, (newValue) => {
  emit('update:modelValue', newValue);
});

// 过滤建议列表
const filteredSuggestions = computed(() => {
  if (!inputValue.value) return suggestions.value;
  
  return suggestions.value.filter(suggestion => 
    suggestion.toLowerCase().includes(inputValue.value.toLowerCase())
  );
});

// 从LocalStorage加载建议
const loadSuggestionsFromStorage = () => {
  const storedSuggestions = localStorage.getItem(props.storageKey);
  if (storedSuggestions) {
    suggestions.value = JSON.parse(storedSuggestions);
  }
};

// 保存建议到LocalStorage
const saveSuggestionsToStorage = () => {
  localStorage.setItem(props.storageKey, JSON.stringify(suggestions.value));
};

// 添加新的建议到历史记录
const addSuggestion = (value: string) => {
  if (!value || value.trim() === '') return;
  
  // 移除已存在的相同值
  const index = suggestions.value.indexOf(value);
  if (index !== -1) {
    suggestions.value.splice(index, 1);
  }
  
  // 添加到列表前面
  suggestions.value.unshift(value);
  
  // 限制历史记录数量
  if (suggestions.value.length > props.maxHistoryItems) {
    suggestions.value = suggestions.value.slice(0, props.maxHistoryItems);
  }
  
  saveSuggestionsToStorage();
};

// 处理输入事件
const handleInput = () => {
  showSuggestions.value = true;
};

// 选择建议
const selectSuggestion = (suggestion: string) => {
  inputValue.value = suggestion;
  showSuggestions.value = false;
};

// 删除建议
const deleteSuggestion = (suggestion: string) => {
  const index = suggestions.value.indexOf(suggestion);
  if (index !== -1) {
    suggestions.value.splice(index, 1);
    saveSuggestionsToStorage();
  }
};

// 清空输入
const clearInput = () => {
  inputValue.value = '';
};

// 延迟隐藏建议，以便点击建议项和删除按钮
const hideSuggestionsDelayed = () => {
  setTimeout(() => {
    showSuggestions.value = false;
  }, 200);
};

// 提交输入值到历史记录
const submitValue = () => {
  if (inputValue.value) {
    addSuggestion(inputValue.value);
  }
};

// 暴露方法给父组件
defineExpose({
  submitValue
});

// 组件挂载时加载建议
onMounted(() => {
  loadSuggestionsFromStorage();
});
</script>

<style scoped>
.auto-complete-input-container {
  position: relative;
  width: 100%;
}

.suggestions-container {
  position: absolute;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  z-index: 99;
  margin-top: 4px;
}

.suggestions-list {
  padding: 0;
  margin: 0;
  list-style: none;
}

.suggestion-item {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.suggestion-item:hover {
  background-color: #f5f7fa;
}

.delete-icon {
  color: #909399;
  cursor: pointer;
}

.delete-icon:hover {
  color: #f56c6c;
}
</style> 